// src/components/pdf-viewer/PDFSearchBox.tsx
//
// Text-search box for the PDF viewer's bottom bar. Drives PDF.js's native
// find controller via the eventBus that react-pdf-highlighter creates
// internally and exposes as `(window as any).PdfViewer`.
//
// PdfHighlighter does NOT instantiate a PDFFindController by default, so we
// lazy-create one and attach it to the running PDFViewer instance the first
// time the user types a query. Subsequent queries dispatch `find` events on
// the same eventBus; results are read back via `updatefindmatchescount` /
// `updatefindcontrolstate` events.
//
// We import PDFFindController via the path that ships with the bundled
// react-pdf-highlighter dependency tree. This is the only place that pdfjs's
// internal viewer module is referenced from app code, so the explicit path is
// kept local to this file. (No new dependencies added — this is the same
// pdfjs-dist build that PdfHighlighter itself uses.)
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Input, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import {
  SearchOutlined,
  CloseOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { VoiceSearchButton } from './VoiceSearchButton';
// eslint-disable-next-line import/no-extraneous-dependencies
import { PDFFindController } from 'react-pdf-highlighter/node_modules/pdfjs-dist/legacy/web/pdf_viewer';

interface PDFSearchBoxProps {
  /** Ref to the loaded pdf document (provided by PdfLoader render-prop). */
  pdfDocumentRef: React.MutableRefObject<any>;
  /** True when the player should render in compact (mobile) layout */
  isMobile?: boolean;
  /** Optional dark-bar styling (white text/borders) when embedded in narration bar. */
  variant?: 'dark' | 'light';
}

interface MatchesCount {
  current: number;
  total: number;
}

export const PDFSearchBox: React.FC<PDFSearchBoxProps> = ({
  pdfDocumentRef,
  isMobile = false,
  variant = 'dark',
}) => {
  const [query, setQuery] = useState('');
  const [matchesCount, setMatchesCount] = useState<MatchesCount>({ current: 0, total: 0 });
  const [findState, setFindState] = useState<number | null>(null);

  const inputRef = useRef<InputRef | null>(null);
  const findControllerRef = useRef<any>(null);
  const eventBusRef = useRef<any>(null);
  const lastQueryRef = useRef<string>('');

  /**
   * Lazily ensure the PDFFindController is wired into the running viewer.
   * Returns true when the controller is ready, false if the viewer isn't
   * mounted yet (e.g. before PdfLoader resolves).
   */
  const ensureFindController = useCallback((): boolean => {
    if (findControllerRef.current) return true;

    const pdfHighlighter = (window as any).PdfViewer;
    if (!pdfHighlighter) return false;
    const eventBus = pdfHighlighter.eventBus;
    const linkService = pdfHighlighter.linkService;
    const viewer = pdfHighlighter.viewer;
    const pdfDocument = pdfDocumentRef.current;
    if (!eventBus || !linkService || !viewer || !pdfDocument) return false;

    const controller = new PDFFindController({ linkService, eventBus });
    controller.setDocument(pdfDocument);
    // Attach to the viewer so its internal scroll-to-match path works,
    // AND propagate the controller to every page's TextHighlighter — they
    // captured `findController = null` at construction (since react-pdf-
    // highlighter never instantiates one), and bail out of `_renderMatches`
    // unless this reference is patched.
    viewer.findController = controller;
    const numPages = pdfDocument.numPages || 0;
    for (let i = 0; i < numPages; i++) {
      const pageView = viewer.getPageView(i);
      if (pageView?.textHighlighter) {
        pageView.textHighlighter.findController = controller;
      }
    }
    // Also patch any pages that get built/rebuilt later (e.g. on scroll
    // into view). PDFViewer fires `textlayerrendered` after a page's
    // textLayer + textHighlighter are constructed.
    eventBus.on('textlayerrendered', (evt: any) => {
      const idx = typeof evt?.pageNumber === 'number' ? evt.pageNumber - 1 : -1;
      if (idx < 0) return;
      const pv = viewer.getPageView(idx);
      if (pv?.textHighlighter && pv.textHighlighter.findController !== controller) {
        pv.textHighlighter.findController = controller;
        // Force a re-render of matches on this page now that the ref is set.
        try {
          pv.textHighlighter._updateMatches?.();
        } catch {
          /* noop */
        }
      }
    });

    findControllerRef.current = controller;
    eventBusRef.current = eventBus;

    // Subscribe to find result events. Using the public `on` method.
    eventBus.on('updatefindmatchescount', (evt: any) => {
      if (evt && evt.matchesCount) {
        setMatchesCount({
          current: evt.matchesCount.current ?? 0,
          total: evt.matchesCount.total ?? 0,
        });
      }
    });
    eventBus.on('updatefindcontrolstate', (evt: any) => {
      if (!evt) return;
      if (evt.matchesCount) {
        setMatchesCount({
          current: evt.matchesCount.current ?? 0,
          total: evt.matchesCount.total ?? 0,
        });
      }
      if (typeof evt.state === 'number') {
        setFindState(evt.state);
      }
    });

    return true;
  }, [pdfDocumentRef]);

  /** Dispatch a find event with the given parameters. */
  const dispatchFind = useCallback(
    (
      type: '' | 'again' | 'highlightallchange',
      q: string,
      findPrevious = false,
    ) => {
      const eventBus = eventBusRef.current;
      if (!eventBus) return;
      eventBus.dispatch('find', {
        source: window,
        type,
        query: q,
        caseSensitive: false,
        entireWord: false,
        phraseSearch: true,
        highlightAll: true,
        findPrevious,
      });
    },
    [],
  );

  /** Clear all search highlights and reset state. */
  const clearSearch = useCallback(() => {
    setQuery('');
    setMatchesCount({ current: 0, total: 0 });
    setFindState(null);
    lastQueryRef.current = '';
    if (eventBusRef.current) {
      // Empty query with highlightAll false scrubs the textLayer highlights.
      eventBusRef.current.dispatch('find', {
        source: window,
        type: '',
        query: '',
        caseSensitive: false,
        entireWord: false,
        phraseSearch: true,
        highlightAll: false,
        findPrevious: false,
      });
    }
  }, []);

  /** Run a brand-new search (called on Enter). */
  const runFind = useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      clearSearch();
      return;
    }
    if (!ensureFindController()) return;
    if (trimmed === lastQueryRef.current) {
      // Same query as last time — treat Enter as "next match".
      dispatchFind('again', trimmed, false);
    } else {
      lastQueryRef.current = trimmed;
      dispatchFind('', trimmed, false);
    }
  }, [query, ensureFindController, dispatchFind, clearSearch]);

  const goNext = useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed) return;
    if (!ensureFindController()) return;
    if (trimmed !== lastQueryRef.current) {
      lastQueryRef.current = trimmed;
      dispatchFind('', trimmed, false);
    } else {
      dispatchFind('again', trimmed, false);
    }
  }, [query, ensureFindController, dispatchFind]);

  const goPrev = useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed) return;
    if (!ensureFindController()) return;
    if (trimmed !== lastQueryRef.current) {
      lastQueryRef.current = trimmed;
      dispatchFind('', trimmed, true);
    } else {
      dispatchFind('again', trimmed, true);
    }
  }, [query, ensureFindController, dispatchFind]);

  /** 🎤 Spoken text fills the box and runs the find. */
  const searchFor = useCallback((text: string) => {
    setQuery(text);
    if (!ensureFindController()) return;
    lastQueryRef.current = text;
    dispatchFind('', text, false);
  }, [ensureFindController, dispatchFind]);

  // Esc clears, Enter runs find, Up/Down navigate matches.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        clearSearch();
        // Blur so subsequent Esc presses don't keep firing.
        inputRef.current?.blur();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey) {
          goPrev();
        } else {
          runFind();
        }
      }
    },
    [clearSearch, runFind, goPrev],
  );

  // If the user empties the input manually (delete-all), wipe highlights.
  useEffect(() => {
    if (query === '' && lastQueryRef.current !== '') {
      clearSearch();
    }
  }, [query, clearSearch]);

  // Cleanup on unmount: clear highlights so they don't persist when the modal
  // closes and reopens against the same window.PdfViewer instance.
  useEffect(() => {
    return () => {
      if (eventBusRef.current && lastQueryRef.current) {
        eventBusRef.current.dispatch('find', {
          source: window,
          type: '',
          query: '',
          caseSensitive: false,
          entireWord: false,
          phraseSearch: true,
          highlightAll: false,
          findPrevious: false,
        });
      }
    };
  }, []);

  // Friendly counter text. Hide until the user has actually searched.
  const counterText = useMemo(() => {
    if (!query.trim() || !lastQueryRef.current) return '';
    if (matchesCount.total === 0 && findState !== null) {
      // PDFFindController FindState.NOT_FOUND === 1
      return findState === 1 ? 'No matches' : '…';
    }
    return `${matchesCount.current} of ${matchesCount.total}`;
  }, [query, matchesCount, findState]);

  // Styling — dark variant blends into the narration bar; light variant for
  // standalone (no-narration) bottom bar.
  const isDark = variant === 'dark';
  const inputStyle: React.CSSProperties = isDark
    ? {
        backgroundColor: 'rgba(255,255,255,0.08)',
        color: '#fff',
        border: '1px solid rgba(255,255,255,0.18)',
      }
    : {};

  const counterStyle: React.CSSProperties = {
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
    minWidth: isMobile ? 0 : 70,
    color: isDark ? 'rgba(255,255,255,0.85)' : '#666',
    whiteSpace: 'nowrap',
  };

  const iconColor = isDark ? '#fff' : undefined;

  // On mobile, the search row stretches full-width (wraps to its own row in
  // the parent's flexWrap container).
  const wrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flex: isMobile ? '1 1 100%' : '0 0 auto',
    minWidth: isMobile ? 0 : 240,
    maxWidth: isMobile ? '100%' : 360,
  };

  return (
    <div style={wrapperStyle} role="search">
      <Input
        ref={inputRef}
        size="small"
        placeholder="Search report…"
        aria-label="Search PDF report"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => {
          // Pre-warm the find controller as soon as the user focuses.
          ensureFindController();
        }}
        prefix={<SearchOutlined style={{ color: iconColor, opacity: 0.7 }} />}
        allowClear={{
          clearIcon: <CloseOutlined style={{ color: iconColor, opacity: 0.7 }} />,
        }}
        suffix={<VoiceSearchButton onResult={searchFor} color={iconColor} />}
        style={{ ...inputStyle, flex: 1 }}
      />
      <Tooltip title="Previous match (Shift+Enter)">
        <Button
          type="text"
          size="small"
          onClick={goPrev}
          disabled={!query.trim() || matchesCount.total === 0}
          icon={<UpOutlined style={{ color: iconColor, fontSize: 12 }} />}
          aria-label="Previous match"
        />
      </Tooltip>
      <Tooltip title="Next match (Enter)">
        <Button
          type="text"
          size="small"
          onClick={goNext}
          disabled={!query.trim() || matchesCount.total === 0}
          icon={<DownOutlined style={{ color: iconColor, fontSize: 12 }} />}
          aria-label="Next match"
        />
      </Tooltip>
      {counterText && <span style={counterStyle}>{counterText}</span>}
    </div>
  );
};
